import { Paper, Stack, Typography } from "@mui/material";

import { formatNumber } from "../../../utility/formatters";
import { OperatorBox } from "./ArcCoOperatorBox";
import ArcCoPaymentSummaryBox from "./ArcCoPaymentSummaryBox";

const PlcContent = ({ plcCalculation }) => {
  const { inputs, outputs, constants } = plcCalculation;
  const { plcPaymentPrice, plcPaymentPricePerAcre, totalPlcPayment, plcPayment } = outputs;
  const isPaymentTriggered = plcPaymentPrice > 0;

  return (
    <Stack className="arc-plc-steps-content">
      {/* Step 1: PLC Payment Rate Calculation */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "colors.borderLightGray",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 1:</span> Calculate PLC Payment Rate
        </Typography>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <ArcCoPaymentSummaryBox
            title="Effective Reference Price"
            value={inputs.effective_reference_price}
            formatOptions={{ places: 2 }}
          />
          <OperatorBox>-</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Effective Price"
            value={Math.max(inputs.national_loan_rate, inputs.market_year_average_price)}
            formatOptions={{ places: 4 }}
          />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="PLC Payment Rate"
            value={plcPaymentPrice}
            formatOptions={{ places: 4 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>
      {/* Step 2: Payment Per Acre Calculation */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "colors.borderLightGray",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 2:</span> Calculate Payment per Acre
        </Typography>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <ArcCoPaymentSummaryBox title="PLC Payment Rate" value={plcPaymentPrice} formatOptions={{ places: 4 }} />
          <OperatorBox>×</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="PLC Yield"
            value={inputs.plc_yield}
            formatType="number"
            formatOptions={{ places: 1 }}
          />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Payment Per Acre"
            value={plcPaymentPricePerAcre}
            formatOptions={{ places: 2 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>
      {isPaymentTriggered && (
        <>
          {/* Step 3 Payment Calculation */}
          <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                borderBottom: 1,
                borderColor: "colors.borderLightGray",
                padding: "14px 18px",
                textAlign: "left",
                "& span": {
                  color: "text.secondary",
                },
              }}
            >
              <span>Step 3:</span> Calculate Total Farm PLC Payment (
              {formatNumber(constants.PLC_PAYMENT_PERCENTAGE * 100, 0)}% of Base Acres)
            </Typography>

            <Stack
              sx={{
                padding: "20px 18px",
                flexDirection: "row",
                alignItems: "stretch",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <ArcCoPaymentSummaryBox
                title="Payment Per Acre"
                value={plcPaymentPricePerAcre}
                formatOptions={{ places: 4 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Base Acres"
                value={inputs.base_acreage}
                formatType="number"
                formatOptions={{ places: 4 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Payment Factor"
                value={constants.PLC_PAYMENT_PERCENTAGE}
                formatType="percentage"
                formatOptions={{ places: 0 }}
              />

              <OperatorBox>=</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Total Farm PLC Payment"
                value={totalPlcPayment}
                formatOptions={{ places: 0 }}
                valueColor="success.main"
              />
            </Stack>
          </Paper>

          {/* Step 4: Final Payment */}
          {inputs.share_percent < 1 && (
            <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  borderBottom: 1,
                  borderColor: "colors.borderLightGray",
                  padding: "14px 18px",
                  textAlign: "left",
                  "& span": {
                    color: "text.secondary",
                  },
                }}
              >
                <span>Step 4:</span> Apply Producer Share Percent
              </Typography>

              <Stack
                sx={{
                  padding: "20px 18px",
                  flexDirection: "row",
                  alignItems: "stretch",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <ArcCoPaymentSummaryBox
                  title={"Total Farm PLC Payment"}
                  value={totalPlcPayment}
                  formatOptions={{ places: 0 }}
                />
                <OperatorBox>×</OperatorBox>
                <ArcCoPaymentSummaryBox
                  title="Share Percent"
                  value={inputs.share_percent}
                  formatType="percentage"
                  formatOptions={{ places: 4 }}
                />
                <OperatorBox>=</OperatorBox>
                <ArcCoPaymentSummaryBox
                  title="Expected Share"
                  value={plcPayment}
                  formatOptions={{ places: 0 }}
                  valueColor="success.main"
                />
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
};

export default PlcContent;
